(() => {
  'use strict';

  const rootPath = window.Shopify?.routes?.root || '/';
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));
  const formatMoney = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency', currency
  }).format((Number(cents) || 0) / 100);

  const bodyLock = (locked) => document.body.classList.toggle('is-locked', locked);

  function initNavigation() {
    const toggle = qs('[data-menu-toggle]');
    const menu = qs('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(willOpen));
      menu.hidden = !willOpen;
      bodyLock(willOpen);
    });
  }

  function initFooter() {
    const groups = qsa('[data-footer-group]');
    if (!groups.length) return;
    const media = window.matchMedia('(max-width: 749px)');
    const sync = () => groups.forEach((group) => { group.open = !media.matches; });
    sync();
    media.addEventListener?.('change', sync);
  }

  const cart = {
    drawer: null,
    backdrop: null,
    loading: null,
    init() {
      this.drawer = qs('[data-cart-drawer]');
      this.backdrop = qs('[data-cart-backdrop]');
      this.loading = qs('[data-cart-loading]');
      if (!this.drawer) return;
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-open]')) this.open();
        if (event.target.closest('[data-cart-close]') || event.target === this.backdrop) this.close();
        const action = event.target.closest('[data-cart-quantity],[data-cart-remove]');
        if (!action) return;
        const line = action.closest('[data-line-key]');
        if (!line) return;
        const quantity = action.hasAttribute('data-cart-remove') ? 0 : Number(action.dataset.cartQuantity);
        this.change(line.dataset.lineKey, quantity);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      });
    },
    open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.backdrop.hidden = false;
      qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      bodyLock(true);
      qs('[data-cart-close]', this.drawer)?.focus();
    },
    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.backdrop.hidden = true;
      qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      bodyLock(false);
    },
    setLoading(active) {
      if (this.loading) this.loading.hidden = !active;
    },
    async change(key, quantity) {
      this.setLoading(true);
      try {
        const response = await fetch(`${rootPath}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Cart update failed');
        await this.refresh();
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
      }
    },
    async refresh() {
      const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be loaded');
      const data = await response.json();
      this.render(data);
      return data;
    },
    render(data) {
      qsa('[data-cart-count]').forEach((node) => {
        node.textContent = `[${data.item_count}]`;
        node.setAttribute('aria-label', `${data.item_count} items`);
      });
      const drawerCount = qs('[data-drawer-count]');
      if (drawerCount) drawerCount.textContent = `[${data.item_count}]`;
      const items = qs('[data-cart-items]');
      const footer = qs('[data-cart-footer]');
      const subtotal = qs('[data-cart-subtotal]');
      if (!items) return;
      if (!data.items.length) {
        items.innerHTML = `<div class="drawer-empty"><p>Your field kit is empty.</p><a href="${rootPath}collections/all">EXPLORE FIELD KITS ↗</a></div>`;
        footer?.classList.add('is-hidden');
        return;
      }
      items.innerHTML = data.items.map((item) => {
        const image = item.featured_image?.url
          ? `<a href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.featured_image.url)}&width=260" width="110" height="130" alt="${escapeHtml(item.product_title)}"></a>` : '';
        const variantTitle = item.variant_title ? `<p>${escapeHtml(item.variant_title)}</p>` : '';
        return `<article class="drawer-line" data-line-key="${escapeHtml(item.key)}">
          ${image}<div><h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>${variantTitle}
          <div class="drawer-line__actions"><button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button><button type="button" data-cart-remove>REMOVE</button></div></div>
          <strong>${formatMoney(item.final_line_price, data.currency)}</strong></article>`;
      }).join('');
      if (subtotal) subtotal.textContent = formatMoney(data.total_price, data.currency);
      footer?.classList.remove('is-hidden');
    }
  };

  function initProduct(root) {
    const json = qs('[data-product-json]', root);
    if (!json) return;
    let variants;
    try { variants = JSON.parse(json.textContent); } catch (error) { console.error('Invalid product variant data', error); return; }

    const allowedSizes = root.dataset.allowedSizes ? root.dataset.allowedSizes.split('|') : [];
    if (allowedSizes.length) variants = variants.filter((variant) => allowedSizes.includes(variant.options[0]));
    const allowedColors = root.dataset.allowedColors ? root.dataset.allowedColors.split('|') : [];
    if (allowedColors.length) variants = variants.filter((variant) => allowedColors.includes(variant.options[1]));
    const currency = root.dataset.currency || 'USD';
    const fieldsets = qsa('[data-option-position]', root);
    const form = qs('[data-product-form]', root);
    const idInput = qs('[data-variant-id]', root);
    const price = qs('[data-product-price]', root);
    const comparePrice = qs('[data-compare-price]', root);
    const availability = qs('[data-availability]', root);
    const sku = qs('[data-sku]', root);
    const addButton = qs('[data-add-to-cart]', root);
    const addLabel = qs('[data-add-label]', root);
    const addPrice = qs('[data-add-price]', root);
    const buyButton = qs('.buy-now', root);
    const mobileBar = qs('[data-mobile-purchase]');
    const mobilePrice = qs('[data-mobile-price]', mobileBar || document);
    const mobileAdd = qs('[data-mobile-add]', mobileBar || document);
    const dynamicCheckout = qs('[data-dynamic-checkout]', root);
    const sizeMapNode = qs('[data-size-map]', root.parentElement || document) || qs('[data-size-map]');
    let sizeMap = {};
    try { if (sizeMapNode) sizeMap = JSON.parse(sizeMapNode.textContent); } catch (_) {}

    const selectedOptions = () => fieldsets.map((fieldset) => qs('input:checked', fieldset)?.value || '');
    const findVariant = () => {
      const selected = selectedOptions();
      return variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
    };
    const updateOptionLabels = () => fieldsets.forEach((fieldset) => {
      const selected = qs('input:checked', fieldset);
      const label = qs('[data-selected-option]', fieldset);
      if (label) label.textContent = selected?.value || 'Select';
    });
    const updateAvailabilityMatrix = () => {
      const current = selectedOptions();
      fieldsets.forEach((fieldset, fieldsetIndex) => {
        qsa('input[data-option-value]', fieldset).forEach((input) => {
          const possible = variants.some((variant) => variant.options.every((option, optionIndex) => optionIndex === fieldsetIndex ? option === input.value : !current[optionIndex] || option === current[optionIndex]));
          input.disabled = !possible;
        });
      });
    };
    const readableMeasurement = (value) => {
      if (value == null || value === '') return '';
      if (typeof value === 'object' && value.value != null) return `${value.value}${value.unit ? ` ${value.unit}` : ''}`;
      return String(value);
    };
    const updateSize = () => {
      const sizeFieldset = fieldsets.find((fieldset) => qs('legend span', fieldset)?.textContent.toLowerCase().includes('size'));
      const value = sizeFieldset ? qs('input:checked', sizeFieldset)?.value : '';
      const measurements = sizeMap[value];
      const variantMeasurements = findVariant()?.measurements || {};
      ['back', 'chest', 'neck'].forEach((key) => {
        const node = qs(`[data-measurement="${key}"]`, root);
        if (node) node.textContent = readableMeasurement(variantMeasurements[key]) || measurements?.[key] || '—';
      });
    };
    const focusVariantMedia = (variant) => {
      const mediaId = variant?.featured_media_id || root.dataset.fallbackMediaId;
      if (!mediaId) return;
      const media = qs(`[data-media-id="${mediaId}"]`, root);
      qsa('[data-media-id]', root).forEach((item) => item.classList.toggle('is-active', item === media));
      if (media && window.matchMedia('(max-width: 989px)').matches) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      qsa('[data-media-target]', root).forEach((thumb) => thumb.classList.toggle('is-active', Number(thumb.dataset.mediaTarget) === Number(mediaId)));
    };
    const renderVariant = (variant, pushState = true) => {
      const active = Boolean(variant);
      const purchasable = Boolean(variant?.available);
      if (idInput) { idInput.value = variant?.id || ''; idInput.disabled = !active; }
      if (price) price.textContent = active ? formatMoney(variant.price, currency) : '—';
      if (addPrice) addPrice.textContent = active ? formatMoney(variant.price, currency) : '';
      if (mobilePrice) mobilePrice.textContent = active ? formatMoney(variant.price, currency) : '—';
      if (comparePrice) {
        const onSale = active && variant.compare_at_price > variant.price;
        comparePrice.textContent = onSale ? formatMoney(variant.compare_at_price, currency) : '';
        comparePrice.classList.toggle('is-hidden', !onSale);
      }
      if (availability) {
        if (!active) availability.textContent = 'UNAVAILABLE / COMBINATION NOT OFFERED';
        else if (!purchasable) availability.textContent = 'SOLD OUT / UNAVAILABLE';
        else if (variant.inventory_management && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) availability.textContent = `LOW STOCK / ${variant.inventory_quantity} REMAINING`;
        else availability.textContent = 'IN STOCK / READY TO SHIP';
      }
      if (sku) sku.textContent = variant?.sku ? `SKU ${variant.sku}` : '';
      [addButton, buyButton, mobileAdd].forEach((button) => { if (button) button.disabled = !purchasable; });
      dynamicCheckout?.classList.toggle('is-unavailable', !purchasable);
      if (addLabel) addLabel.textContent = !active ? 'UNAVAILABLE' : purchasable ? 'ADD TO CART' : 'SOLD OUT';
      if (active && pushState && history.replaceState) history.replaceState({}, '', `${root.dataset.productUrl}?variant=${variant.id}`);
      focusVariantMedia(variant);
    };
    const onChange = () => {
      updateOptionLabels(); updateAvailabilityMatrix(); updateSize(); renderVariant(findVariant());
    };

    root.addEventListener('change', (event) => { if (event.target.matches('.variant-input')) onChange(); });
    qsa('[data-media-target]', root).forEach((thumb) => thumb.addEventListener('click', () => {
      const media = qs(`[data-media-id="${thumb.dataset.mediaTarget}"]`, root);
      qsa('[data-media-id]', root).forEach((item) => item.classList.toggle('is-active', item === media));
      if (window.matchMedia('(max-width: 989px)').matches) media?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      qsa('[data-media-target]', root).forEach((item) => item.classList.toggle('is-active', item === thumb));
    }));

    const quantity = qs('[data-quantity-input]', root);
    qs('[data-quantity-minus]', root)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value) - 1); });
    qs('[data-quantity-plus]', root)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value) + 1); });
    mobileAdd?.addEventListener('click', () => addButton?.click());

    form?.addEventListener('submit', async (event) => {
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      if (!findVariant()?.available) return;
      const message = qs('[data-form-message]', form);
      addButton.disabled = true;
      if (addLabel) addLabel.textContent = 'ADDING…';
      try {
        const response = await fetch(`${rootPath}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This field kit could not be added.');
        await cart.refresh(); cart.open();
        if (message) message.textContent = 'Added to your field kit.';
      } catch (error) {
        if (message) message.textContent = error.message;
      } finally {
        const variant = findVariant();
        addButton.disabled = !variant?.available;
        if (addLabel) addLabel.textContent = variant?.available ? 'ADD TO CART' : 'UNAVAILABLE';
      }
    });

    const guide = qs('[data-size-guide]', root);
    qs('[data-size-guide-open]', root)?.addEventListener('click', () => guide?.showModal());
    qs('[data-size-guide-close]', root)?.addEventListener('click', () => guide?.close());
    guide?.addEventListener('click', (event) => { if (event.target === guide) guide.close(); });

    updateOptionLabels(); updateAvailabilityMatrix(); updateSize(); renderVariant(findVariant(), false);
  }

  function initLightbox() {
    const dialog = qs('[data-lightbox]');
    const image = qs('[data-lightbox-image]', dialog || document);
    if (!dialog || !image) return;
    let sources = [];
    let index = 0;
    let touchStart = 0;
    const show = (nextIndex) => {
      if (!sources.length) return;
      index = (nextIndex + sources.length) % sources.length;
      image.src = sources[index];
    };
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-lightbox-open]');
      if (!opener) return;
      const gallery = opener.closest('[data-product-gallery]');
      const openers = qsa('[data-lightbox-open]', gallery || document);
      sources = openers.map((item) => item.dataset.imageSrc).filter(Boolean);
      index = Math.max(0, openers.indexOf(opener));
      show(index); dialog.showModal(); bodyLock(true);
    });
    qs('[data-lightbox-close]', dialog)?.addEventListener('click', () => dialog.close());
    qs('[data-lightbox-prev]', dialog)?.addEventListener('click', () => show(index - 1));
    qs('[data-lightbox-next]', dialog)?.addEventListener('click', () => show(index + 1));
    dialog.addEventListener('close', () => bodyLock(false));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0].clientX; }, { passive: true });
    dialog.addEventListener('touchend', (event) => {
      const delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) > 45) show(index + (delta < 0 ? 1 : -1));
    }, { passive: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initNavigation(); initFooter(); cart.init(); initLightbox(); qsa('[data-product-root]').forEach(initProduct);
  });
})();
